import {measurementStart} from "../measurements-component/measurement-interval";
import {
    Component,
    ElementRef,
    inject,
    Input,
    OnChanges,
    OnInit,
    SimpleChanges,
    ViewChild,
    ViewContainerRef
} from "@angular/core";
import {BubbleDataPoint, ChartDataset, ChartTypeRegistry, Point} from "chart.js";
import {
    AggregatedDataPoint,
    AlertType,
    BuildingType,
    Connection,
    ConnectionType,
    DataType,
    Location,
    MeasurementsResult,
    MeterType,
    TimeRange
} from "@flowmaps-com/flowmaps-typescriptmodels";
import {ChartDataPerMeasurement, ChartDataProvider, DashboardTime, Slot} from "../../utils/chart-data-provider";
import {ChartType, TooltipModel} from "chart.js/dist/types";
import {View} from "../../common/view";
import {Handler} from "../../common/handler";
import {ChartModalOptions, ChartUtilsService} from "./chart-utils.service";
import {AppContext, CompletenessInfo} from "../../app-context";
import {PaginationComponent} from "../../common/pagination/pagination.component";
import {ChartCompare, ChartOptions, ChartOptionType} from "../dashboard/dashboard.types";
import {
    DataQueryFilters,
    MeasurementsHandlerOpenModalCommand
} from "../measurements-component/measurements-handler.component";
import {SourceInfo} from "../../utils/source-providers/sources-provider";
import {interpolate, lodash, nonNull} from "../../common/utils";
import {Entity, EntityType} from "../../handlers/entity";
import {combineLatest, distinctUntilChanged, Observable, of, take} from "rxjs";
import {sendCommandAndForget} from "../../common/app-common-utils";
import {DashboardContext} from "../dashboard/dashboard.context";
import {TranslateDirective} from "../../common/utils/translate.directive";
import {
    CALCULATED_RESIDUAL_COLOR,
    calculatedResidualEntity,
    effectiveMeterType,
    isCalculatedResidual,
    isChargePointMeter,
    isMeterVisible
} from '../refdata/meters/submeter-role.utils';
import moment from "moment/moment";
import {cloneDeep, groupBy} from "lodash";
import {Rgba} from "../../common/rgba";
import {Measurements} from "../measurements-component/measurements";
import {RefdataUtils} from "../refdata/refdata-utils";
import {filter} from "rxjs/operators";
import {meterOutputSettingsFingerprint} from './meter-output-settings-fingerprint';
import {changePowerSelection} from './measurement-feature-refresh';
import {canUseDashboardTotals, dashboardDataMatchesFilters} from '../measurements-component/refreshable-measurements';
import {shouldAutoEnablePeakPower} from './power-default';
import {buildMeterChartLookups, MeterChartLookups} from './meter-chart-lookups';
import {
    belongsToResidualConnection,
    calculatedResidualLabel,
    calculateResidualSeries,
    canCalculateResidual,
    residualConnectionId
} from './calculated-residual';

@Component({
    template: "",
})
@Handler()
export class BaseMeasurementChartComponent extends View implements OnInit, OnChanges {
    chartUtils = inject(ChartUtilsService);
    appContext = AppContext;
    @Input() showInReport: boolean;
    @Input() showInQuarterlyReport: boolean;
    @Input() quarterlyReportDisplayTimeRange: DashboardTime;
    @Input() showConsumptionPerDegreeDay: boolean = false;
    @ViewChild('container', {read: ViewContainerRef}) container: ViewContainerRef;
    @ViewChild("pagination") pagination: PaginationComponent<any>;

    completenessInfo: ChartCompleteness;
    chartOptionType: ChartOptionType;
    mainConsumptionType: DataType;
    mainConnectionType: ConnectionType;
    connectionTypes: ConnectionType[] = [];
    meterDashboard: boolean;
    chartDataProvider: ChartDataProvider = new ChartDataProvider();
    tableElement: ElementRef;
    options: ChartOptions;
    modalOptions: ChartModalOptions = {
        fullScreen: false,
    }
    isPortfolio: boolean;
    filters: DataQueryFilters;
    timeRange: DashboardTime;
    comparedTimeRange: DashboardTime;
    quarterlyReportSourceTimeRange: DashboardTime;
    selectedConnections: SourceInfo[] = [];
    feedInPowerTypes: DataType[] = [];
    contractedCapacityRequiredForPower: boolean;
    hasProductionMeter: boolean;
    groupByEntityId: boolean;
    onlyMetersSelected: boolean;

    private liveLocations = new Map<string, Location>();
    private liveLocationFingerprints = new Map<string, string>();
    private watchedLocationIds = new Set<string>();
    private locationEntitiesSubscriptionStarted = false;
    private powerDefaultApplied = false;

    _data: MeasurementDataset[];
    _rawData: ChartDataPerMeasurement;
    _rawPreviousData: ChartDataPerMeasurement;
    private dashboardMeasurementsData: ChartDataPerMeasurement;

    private uniqueId = lodash.uniqueId();
    getInputId = (id: string) => `${this.uniqueId}-${id}`;

    get isReportMode(): boolean {
        return !!(this.showInReport || this.showInQuarterlyReport);
    }

    @Input()
    set data(d: ChartModalOptions) {
        this.modalOptions = d;
    }

    ngOnInit() {
        this.subscribeTo("getChartOptions", this.chartOptionType).subscribe((opts: ChartOptions) => {
            opts.showAllDays = opts.showAllDays === undefined ? true : opts.showAllDays;
            opts.selectedDataType = opts.selectedDataType || this.mainConsumptionType;
            opts.selectedDays = opts.selectedDays || [];
            opts.showIncidents = false;
            opts.selectedIncidents = [];
            opts.selectedWeatherTypes = opts.selectedWeatherTypes || [];
            // Keep the shared dashboard options reference. A chart can be recreated when
            // its last visible dataset is disabled; copying the options here would then
            // restore the old checkbox values from the dashboard wrapper.
            this.options = opts;
            this.applyPowerDefault();
            if (this.showConsumptionPerDegreeDay) {
                this.options.showConsumptionPerDegreeDay = true;
            }
        });
        this.subscribeTo("isPortfolio").subscribe(isPortfolio => {
            this.isPortfolio = isPortfolio;
            this.applyPowerDefault();
            this.sendQuery("groupByEntity").subscribe(groupByEntity => {
                this.groupByEntityId = groupByEntity;
                if (!this.groupByEntityId && groupByEntity && this.chartOptionType != ChartOptionType.Water) {
                    this.options.groupByEntityId = true;
                }
            });
        });
        combineLatest([this.subscribeTo("getChartDataQueryFilters")
            .pipe(filter(f => !DashboardContext.hasSelectedSources() || f.allSelectedSources?.length > 0)),
            this.subscribeTo("isMeterDashboard").pipe(distinctUntilChanged())])
            .subscribe(([filters, isMeterDashboard]) => {
            this.meterDashboard = isMeterDashboard;
            this.applyPowerDefault();
            if (this.meterDashboard && this.options) {
                this.options.groupByEntityIdPossible = true;
                this.groupByEntityId = true;
                this.options.groupByEntityId = true;
            }
            this.refreshAfterFilterChange(filters);
        });
        if (!this.isReportMode) {
            this.subscribeTo("getDashboardMeasurements").subscribe((data: ChartDataPerMeasurement) => {
                this.dashboardMeasurementsData = data;
                if (this.usesSharedDashboardMeasurements() && this.measurementDataMatches(data, this.timeRange)) {
                    this.setData(data, data.timeRange);
                }
            });
        }
    }

    private applyPowerDefault() {
        const supportsPower = shouldAutoEnablePeakPower(this.chartOptionType);
        const primaryConnections = this.activePrimaryConnections(this.mainConnectionType);
        const canShowPower = this.meterDashboard === false && supportsPower
            && primaryConnections.length === 1
            && (this.isPortfolio !== true || this.getSelectedConnections().length === 1);
        if (!canShowPower) {
            this.powerDefaultApplied = false;
            return;
        }
        if (!this.powerDefaultApplied && this.options) {
            this.options.showPower = true;
            this.powerDefaultApplied = true;
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        if (!this.filters) {
            return;
        }
        if (changes['showInQuarterlyReport'] || changes['quarterlyReportDisplayTimeRange']) {
            this.refreshAfterFilterChange(this.filters);
        }
    }

    protected refreshAfterFilterChange(f: DataQueryFilters) {
        this.filters = f;
        this.quarterlyReportSourceTimeRange = this.showInQuarterlyReport ? f.timeRange : null;
        this.timeRange = f.timeRange;
        if (f.timeRange) {
            if (f.allSelectedSources?.length) {
                const allSelectedSources = f.allSelectedSources || [];
                this.selectedConnections = allSelectedSources.filter(s => this.connectionTypes.includes(s.connectionType) && s.type === EntityType.connection);
                this.applyPowerDefault();
                this.hasProductionMeter = allSelectedSources.some(s => s.type === EntityType.meter && s.meterType === MeterType.GROSS_PRODUCTION);
                this.onlyMetersSelected = allSelectedSources.every(s => s.type === EntityType.meter) ||
                    (allSelectedSources.length === 1 && allSelectedSources[0].type === EntityType.connection && allSelectedSources[0].source.connection.meters.some(m => m.info.type == MeterType.INTERMEDIATE));
                this.options.groupByEntityIdPossible = allSelectedSources.some(s => s.type === EntityType.meter
                    && MeterType.INTERMEDIATE === s.meterType
                    && this.connectionTypes.includes((<Connection>s.source.connection).info.connectionType as ConnectionType)) || this.meterDashboard;
                if (this.onlyMetersSelected || this.meterDashboard) {
                    this.groupByEntityId = this.options.groupByEntityId = true;
                }
            }
            this.watchSelectedLocations(f);
            if (this.showInQuarterlyReport && this.options?.compare?.enabled) {
                this.refreshQuarterlyReportData();
                return;
            }
            if (this.usesSharedDashboardMeasurements()) {
                if (this.measurementDataMatches(this.dashboardMeasurementsData, this.timeRange)) {
                    this.setData(this.dashboardMeasurementsData, this.timeRange);
                }
            } else {
                this.getChartData(this.timeRange).subscribe((d: ChartDataPerMeasurement) => this.setData(d, this.timeRange));
            }
            this.refreshComparedData();
        } else {
            this.watchSelectedLocations(f);
        }
    }

    private watchSelectedLocations(filters: DataQueryFilters) {
        this.watchedLocationIds.clear();
        // Live meter metadata is only consumed when this chart receives per-meter datasets.
        if (!filters?.timeRange || !this.connectionTypes.includes(ConnectionType.Electricity)
            || !this.groupByEntityId) {
            this.liveLocations.clear();
            this.liveLocationFingerprints.clear();
            return;
        }
        const locations = new Map<string, Location>();
        [...(filters.allSelectedSources || []), ...(filters.cleanedSources || [])].forEach(source => {
            const location = source.source?.location;
            if (location?.locationId && !locations.has(location.locationId)) {
                locations.set(location.locationId, location);
            }
        });
        locations.forEach(location => {
            this.watchedLocationIds.add(location.locationId);
            if (!this.liveLocations.has(location.locationId)) {
                this.liveLocations.set(location.locationId, location);
            }
            if (!this.liveLocationFingerprints.has(location.locationId)) {
                this.liveLocationFingerprints.set(location.locationId,
                    meterOutputSettingsFingerprint(location));
            }
        });
        this.removeUnselectedLocationState();
        if (this.locationEntitiesSubscriptionStarted || !this.watchedLocationIds.size) {
            return;
        }
        this.locationEntitiesSubscriptionStarted = true;
        this.subscribeTo("getEntities").subscribe((entities: Map<string, Entity>) => {
            this.watchedLocationIds.forEach(locationId => {
                const entity = entities?.get(locationId);
                if (!entity?.location) {
                    return;
                }
                // Keep an immutable fingerprint instead of comparing Location object references.
                // The entity stream may update a Location in place, which made both sides of the
                // previous comparison identical and left the electricity chart with stale data.
                const nextFingerprint = meterOutputSettingsFingerprint(entity.location);
                const previousFingerprint = this.liveLocationFingerprints.get(locationId);
                const meterSettingsChanged = previousFingerprint !== nextFingerprint;
                this.liveLocationFingerprints.set(locationId, nextFingerprint);
                this.liveLocations.set(locationId, entity.location);
                if (meterSettingsChanged && this.filters?.timeRange) {
                    // Visibility and role changes alter both the returned meter series and the
                    // calculated residual. Reprocessing the existing raw data would leave hidden
                    // meters and the old residual in place, so request a fresh result instead.
                    this.sendCommandAndForget("refreshDashboardMeasurements");
                }
            });
        });
    }

    private removeUnselectedLocationState() {
        this.liveLocations.forEach((_location, locationId) => {
            if (!this.watchedLocationIds.has(locationId)) {
                this.liveLocations.delete(locationId);
                this.liveLocationFingerprints.delete(locationId);
            }
        });
    }

    private usesSharedDashboardMeasurements(): boolean {
        const features = this.measurementQueryFeatures();
        return !this.isReportMode && !features.includeWeather && !features.includeClimateNormalization
            && !features.includeElectricityConnectionContext
            && this.options?.showIncidents !== true && !this.usesDashboardTotals(features);
    }

    private measurementDataMatches(data: ChartDataPerMeasurement, timeRange: DashboardTime): boolean {
        return !!data?.timeRange && !!timeRange
            && data.timeRange.start === timeRange.start
            && data.timeRange.end === timeRange.end
            && dashboardDataMatchesFilters(data, timeRange,
                this.filters?.cleanedSources ?? this.filters?.sources);
    }

    private getChartData = (timeRange?: DashboardTime) => {
        const features = this.measurementQueryFeatures();
        const {
            includeWeather, includePower, includeClimateNormalization, includeElectricityConnectionContext
        } = features;
        const useTotals = this.usesDashboardTotals(features);
        return this.sendQuery(useTotals ? "getTotals" : "getMeasurementsData", {
            range: timeRange,
            showIncidents: this.options.showIncidents,
            includeWeather,
            includePower,
            includeClimateNormalization,
            includeElectricityConnectionContext
        });
    };

    protected measurementQueryFeatures() {
        return {
            includeWeather: (this.options?.selectedWeatherTypes?.length || 0) > 0,
            includePower: this.options?.showPower === true || this.options?.showFeedInPower === true,
            includeClimateNormalization: this.options?.showConsumptionPerDegreeDay === true
                || (!!this.options?.gasConsumptionView && this.options.gasConsumptionView !== 'actual'),
            includeElectricityConnectionContext: false
        };
    }

    protected usesDashboardTotals(features = this.measurementQueryFeatures()): boolean {
        return canUseDashboardTotals({
            ...features,
            groupByEntityId: this.groupByEntityId,
            groupByEntityIdPossible: this.options?.groupByEntityIdPossible,
            reportMode: this.isReportMode,
            hasDashboardSelectedSources: DashboardContext.hasSelectedSources(),
            hasFilterSources: !!this.filters?.sources?.length,
            meterDashboard: this.meterDashboard,
            portfolio: this.isPortfolio
        });
    }

    private getCompleteness = (timeRange?: DashboardTime): Observable<CompletenessInfo[]> => {
        return this.sendQuery("getCompleteness", timeRange);
    }

    openModal(type: typeof BaseMeasurementChartComponent) {
        this.sendCommandAndForget("openChartInModal", <MeasurementsHandlerOpenModalCommand>{
            component: type,
            chartOptions: {
                [this.chartOptionType]: this.options
            },
            chartModalData: this.modalOptions,
            container: this.container,
            modalClasses: "modal-dialog-centered modal-xl d-flex align-items-stretch"
        });
    }

    closeModal = () => sendCommandAndForget("closeModal");

    getSelectedEans = (): string[] =>
        this.activePrimaryConnections()
            .map(c => c.source.connection.info.code);


    getSelectedConnections = (connectionType?: ConnectionType) => connectionType
        ? this.selectedConnections.filter(s => s.connectionType === connectionType) : this.selectedConnections;

    contractedCapacity(connectionType: ConnectionType, powerType?: DataType) {
        const connections = this.getSelectedConnections(connectionType);
        if (this.powerAllowed(connectionType)) {
            const capacity = (connection: Connection) => this.contractedCapacityForPowerType(connection, powerType);
            if (this.isPortfolio) {
                return connections.length === 1 ? capacity(connections[0]?.source.connection) : null;
            } else {
                const connection = connections.find(c => c.source.connection.info.code === this.selectedPowerConnectionCode(connectionType))
                    ?.source.connection;
                return capacity(connection);
            }
        }
        return null;
    }

    private contractedCapacityForPowerType(connection: Connection, powerType?: DataType) {
        if (!connection) {
            return null;
        }
        if (powerType === DataType.electricityFeedInPower && connection.info?.connectionType === ConnectionType.Electricity) {
            return connection.info.contractedFeedInCapacity;
        }
        return connection.contractedCapacity;
    }

    measurementUnit = () => DashboardContext.getMeasurementUnit(this.mainConsumptionType);

    get powerAxisTitle(): string {
        const hasWeatherSelected = this.options.selectedWeatherTypes?.length > 0;
        const hasPowerSelected = this.activePrimaryConnections().length === 1
            && (this.options.showPower || this.options.showFeedInPower);
        const connectionType = this.getSelectedConnections()[0]?.connectionType;
        return !hasWeatherSelected && hasPowerSelected && connectionType
            ? DashboardContext.getMeasurementUnit(this.powerDataTypes(connectionType)[0]) : null;
    }

    get y2AxisTitle(): string {
        return this.options.selectedWeatherTypes?.length === 1
            ? DashboardContext.getMeasurementUnit(this.options.selectedWeatherTypes[0]) : null;
    }

    hasDataOfTypes = (dataType: DataType | DataType[]): boolean => {
        const dataTypes = lodash.isArray(dataType) ? dataType : [dataType];
        return [this._rawData?.measurements, this._rawData?.estimatedMeasurements,
            this._rawPreviousData?.measurements, this._rawPreviousData?.estimatedMeasurements].filter(nonNull)
            .some(d => Object.keys(d).some(k => dataTypes.includes(k as DataType)));
    }

    powerAllowed = (connectionType: ConnectionType): boolean => this.isPortfolio
        ? this.getSelectedConnections(connectionType).length === 1 : !!this.selectedPowerConnectionCode(connectionType);

    hasPower = (connectionType?: ConnectionType): boolean => connectionType
        ? this.hasDataOfTypes(this.powerDataTypes(connectionType))
        : Object.keys(this.powerDataTypeMapped())
            .some(connectionType => this.hasDataOfTypes(this.powerDataTypes(connectionType as ConnectionType)));

    getDataFromMeasurements = (data: { [P in DataType]?: MeasurementDataset[] }): MeasurementDataset[] => {
        return Object.values(data).flatMap(v => v).filter(v => v);
    }

    refreshData = (emit?: boolean) => {
        this._data = [];
        if (this._rawData) {
            this._data = this.filterMeasurementDatasetsForDisplay(this.transformAggregatedMeasurementDatasets(this.getConsumptionAndProductionData(this.transformMeasurementDatasets(this.getDataFromMeasurements(this._rawData.measurements), false, false)), false, false))
                .concat(this.filterMeasurementDatasetsForDisplay(this.transformAggregatedMeasurementDatasets(this.getConsumptionAndProductionData(this.transformMeasurementDatasets(this.getDataFromMeasurements(this._rawData.estimatedMeasurements), false, true)), false, true)));
            if (this.options.showCompleteness && this._rawData?.completeness) {
                this._data = this._data.concat(this.getCompletenessAxis(this._rawData.completeness));
            }
            this._data = this._data.concat(this.options.selectedWeatherTypes.map(dataType => this.getWeatherDataset(dataType, this.timeRange, false)));
            if (this._rawData.incidents?.length) {
                this._data = this._data.concat(this.createIncidentsDatasets(this._rawData));
            }
        }
        if (this._rawPreviousData) {
            this._data = this._data.concat(this.filterMeasurementDatasetsForDisplay(this.transformAggregatedMeasurementDatasets(this.getConsumptionAndProductionData(this.transformMeasurementDatasets(this.getDataFromMeasurements(this._rawPreviousData.measurements), true, false)), true, false)))
                .concat(this.filterMeasurementDatasetsForDisplay(this.transformAggregatedMeasurementDatasets(this.getConsumptionAndProductionData(this.transformMeasurementDatasets(this.getDataFromMeasurements(this._rawPreviousData.estimatedMeasurements), true, true)), true, true)));
            if (this.options.showCompleteness && this._rawPreviousData?.completeness) {
                this._data = this._data.concat(this.getCompletenessAxis(this._rawPreviousData.completeness, true,
                    ChartUtilsService.getComparedYear(this.options.compare, this.timeRange)));
            }
            this._data = this._data.concat(this.options.selectedWeatherTypes.map(dataType => this.getWeatherDataset(dataType, this.comparedTimeRange, true)));
        }
        this._data = this._data.concat(this.addConnectionTypeSpecificData(this.timeRange));
        this._data = this.applyQuarterlyReportCutoff(this._data);
        this.updateCompleteness();
        if (emit) {
            const displayTimeRange = this.getDisplayTimeRange();
            const yAxisDataTypes = this.measurementTypes().concat(this.productionDataTypes());
            this.chartDataProvider.emit({
                datasets: this._data.filter(d =>
                    !yAxisDataTypes.includes(d.measurementType) || d.dataset.yAxisID === this.getYAxis()),
                labels: Measurements.getSlots(displayTimeRange).map(s => s.label)
            });
        }
    }

    get displayTimeRange(): DashboardTime {
        return this.getDisplayTimeRange();
    }

    protected transformMeasurementDatasets(datasets: MeasurementDataset[], isPrevious: boolean, isEstimated: boolean): MeasurementDataset[] {
        return datasets;
    }

    /** Presentation views run after aggregation, which may rebuild source labels. */
    protected transformAggregatedMeasurementDatasets(datasets: MeasurementDataset[], isPrevious: boolean, isEstimated: boolean): MeasurementDataset[] {
        return datasets;
    }

    protected filterMeasurementDatasetsForDisplay(datasets: MeasurementDataset[]): MeasurementDataset[] {
        return datasets;
    }

    createIncidentsDatasets(chartData: ChartDataPerMeasurement): any[] {
        const datasets: MeasurementDataset<'scatter'>[] = [];
        const slots: Slot[] = Measurements.getSlots(this.timeRange);
        const incidentIcon = new Image();
        incidentIcon.src = '/assets/images/warning-triangle.svg';
        const incidents = this.getRelevantIncidentEntries(chartData)
            .map(({entity, alertType, dataPoints}) => ({
                entity,
                alertType,
                dataPoints: this.filterDataPointsWithinRange(dataPoints, {
                    start: moment(this.timeRange.start).valueOf(),
                    end: moment(this.timeRange.end).valueOf()
                })
            }))
            .filter(incident =>
                incident.dataPoints.length > 0 &&
                (this.options.showGrossProduction || incident.entity.meter.info.type !== 'GROSS_PRODUCTION') &&
                (this.onlyMetersSelected || incident.entity.meter.info.type !== 'INTERMEDIATE')
            );

        const incidentsPerSlot = slots.map(slot => {
            const slotRange = {start: slot.startTime, end: slot.endTime};
            return incidents.flatMap(({entity, alertType, dataPoints}) =>
                this.filterDataPointsWithinRange(dataPoints, slotRange).map(dp =>
                    ({entity, alertType, timeRange: dp.timeRange})));
        });
        const accumulatedValues = this.getAccumulatedData(this.groupByEntityId ? chartData.byEntity : chartData.totals, this.timeRange, true);
        const consumptionValues = this.getAccumulatedData(this.groupByEntityId ? chartData.byEntity : chartData.totals, this.timeRange, false);
        const offset = accumulatedValues.length > 0 ? Math.max(...accumulatedValues) * 0.1 : 0;

        slots.forEach((slot, slotIndex) => {
            const maxIncidentsPerSlot = 10;
            const slotMax = consumptionValues[slotIndex] ?? 0;
            const yValue = slotMax + offset;
            const incidentsInSlot = incidentsPerSlot[slotIndex];
            const incidentsToShow = incidentsInSlot.length <= maxIncidentsPerSlot
                ? incidentsInSlot
                : (() => {
                    const grouped = groupBy(incidentsInSlot, a => a.alertType);
                    const contractedPower = (grouped['ContractedPowerExceeded'] ?? []).slice(0, 4);
                    const contractedFeedInPower = (grouped['ContractedFeedInPowerExceeded'] ?? []).slice(0, 1);
                    const disconnected = (grouped['Disconnected'] ?? []).slice(0, 4);
                    const selected = [...disconnected, ...contractedPower, ...contractedFeedInPower];
                    const missingData = (grouped['MissingData'] ?? [])
                        .slice(0, maxIncidentsPerSlot - selected.length);
                    return [...selected, ...missingData];
                })();
            const hidden = incidentsInSlot.length - incidentsToShow.length;
            incidentsToShow.forEach(({alertType, entity }) => {
                const data = [{x: slot.label, y: yValue,  entity}];
                const tooltipLabels = [slot.label];
                const color = AppContext.getAlertConfig(alertType as AlertType).color;
                datasets.push({
                    measurementType: alertType as any,
                    entityId: entity.meter.meterId,
                    estimated: false,
                    dataset: {
                        label: TranslateDirective.getTranslation(alertType.toLowerCase()),
                        data,
                        type: 'scatter',
                        yAxisID: 'yScatter',
                        fill: false,
                        spanGaps: true,
                        order: AppContext.indexOfDataType(alertType as DataType),
                        measurementType: alertType as any,
                        tooltipLabels,
                        backgroundColor: color,
                        tooltip: {
                            formatter: () => `${RefdataUtils.addressFormatter(entity.location.info.address, false)} - ${entity.connection.info.code} (${entity.connection.info.surveyor.name})`,
                            borderColors: [color],
                            stack: "Alerts",
                        },
                        stack: "Current period",
                        pointStyle: incidentIcon,
                        pointRadius: 10,
                        pointHoverRadius: 10,
                        pointHitRadius: 0,
                        showLine: false,
                        clip: false,
                        ...(hidden ? {
                            footerText: TranslateDirective.getTranslation("incidentfooter", "", { count: hidden })
                        } : {})

                    } as any
                });
            });
        });
        return datasets;
    }

    private getRelevantIncidentEntries(chartData: ChartDataPerMeasurement) {
        return chartData.incidents
            .filter(a => this.connectionTypes.includes(a.entity?.connection?.info.connectionType as ConnectionType))
            .flatMap(incident => {
                if (!incident.incidents) return [];
                return Object.entries(incident.incidents)
                    .filter(([alertType, dataPoints]) =>
                        dataPoints && dataPoints.length > 0 &&
                        this.options.selectedIncidents?.some(option => option.value === alertType))
                    .map(([alertType, dataPoints]) =>
                        ({entity: incident.entity, alertType, dataPoints}));
            });
    }

    private filterDataPointsWithinRange(dataPoints: AggregatedDataPoint[], range: { start: number, end: number }): AggregatedDataPoint[] {
        return dataPoints.filter(dp => {
            const dpStart = moment(measurementStart(dp)).valueOf();
            return dpStart >= range.start && dpStart < range.end;
        });
    }

    protected getAccumulatedData(data: MeasurementsResult[], dateRange: DashboardTime, withProductionData: boolean): number[] {
        const relatedTypes = [
            ...this.measurementTypes(),
            ...(withProductionData ? Object.values(this.productionDataTypesMapped()).flat() : [])
        ];
        const merged: MeasurementsResult = {
            measurements: {},
            estimatedMeasurements: {}
        };
        for (const result of data) {
            for (const type of relatedTypes) {
                let m = result.measurements?.[type] ?? [];
                let e = result.estimatedMeasurements?.[type] ?? [];
                const factor = this.measurementDisplayFactor(type);
                if (factor !== 1) {
                    m = m.map(dp => ({ ...dp, value: dp.value * factor }));
                    e = e.map(dp => ({ ...dp, value: dp.value * factor }));
                }
                if (m.length > 0) {
                    merged.measurements![type] = (merged.measurements![type] ?? []).concat(m);
                }
                if (e.length > 0) {
                    merged.estimatedMeasurements![type] = (merged.estimatedMeasurements![type] ?? []).concat(e);
                }
            }
        }
        const allDataPoints: AggregatedDataPoint[] = relatedTypes.flatMap(type => [
            ...(merged.measurements?.[type] ?? []),
            ...(merged.estimatedMeasurements?.[type] ?? [])
        ]);
        const chartData = Measurements.dataPointsToChartData(allDataPoints);
        const grouped = Measurements.groupByTimeFrames(chartData, dateRange, AppContext.getAggregationMethod(this.mainConsumptionType));
        return grouped.data;
    }

    incidentOptions: IncidentOption[] = INCIDENT_OPTIONS;

    incidentsChange(selected: IncidentOption[]) {
        this.options.selectedIncidents = selected;
        this.options.showIncidents = selected.length > 0;
        this.refreshAfterFilterChange(this.filters);
    }

    showIncidentsChange($event: any) {
        this.options.showIncidents = $event;
        this.options.selectedIncidents = !$event ? [] : this.incidentOptions
        this.refreshAfterFilterChange(this.filters);
    }

    private updateCompleteness = () => {
        this.completenessInfo = this._rawData?.completeness ? {
            title: AppContext.completenessTitle(this._rawData.completeness, this.connectionTypes),
            color: AppContext.completenessColor(this._rawData.completeness, this.connectionTypes),
            completeness: AppContext.completeness(this._rawData.completeness, this.connectionTypes)
        } : null;
    }

    private addConnectionTypeSpecificData(timeRange: DashboardTime): MeasurementDataset[] {
        let datasets: MeasurementDataset[] = [];
        datasets = this.connectionTypes.flatMap(c => this.addPowerData(datasets, timeRange, c))
            .filter(a => a);
        return datasets;
    }

    private addPowerData(datasets: MeasurementDataset[], timeRange: DashboardTime, connectionType: ConnectionType) {
        const contractedRequired = this.contractedCapacityRequiredForPower;
        const hasContractedCapacity = this.powerDataTypes(connectionType)
            .some(powerType => this.shouldRenderPowerType(connectionType, powerType)
                && this.contractedCapacity(connectionType, powerType));
        const hasFeedInPowerData = this.powerDataTypes(connectionType)
            .some(powerType => this.shouldRenderPowerType(connectionType, powerType)
                && this.feedInPowerTypes.includes(powerType)
                && this.hasDataOfTypes(powerType));
        if ((contractedRequired && (hasContractedCapacity || hasFeedInPowerData)) || !contractedRequired) {
            datasets = datasets.concat(this.addPowerToChart(timeRange, connectionType));
        }
        return datasets;
    }

    addPowerToChart(timeRange: DashboardTime, connectionType: ConnectionType): MeasurementDataset[] {
        const datasets: MeasurementDataset[] = [];
        if (this.powerAllowed(connectionType)) {
            this.powerDataTypes(connectionType).forEach(powerType => {
                const contractedCapacity = this.contractedCapacity(connectionType, powerType);
                if (!this.shouldRenderPowerType(connectionType, powerType)) {
                    return;
                }
                if (this.hasDataOfTypes(powerType)) {
                    datasets.push(this.getPowerDataset(powerType));
                }
                if (contractedCapacity) {
                    const contractedCapacityColor = "#a2a2a2";
                    datasets.push({
                        dataset: {
                            type: "line",
                            borderDash: [5],
                            order: AppContext.indexOfDataType(powerType),
                            yAxisID: "power",
                            spanGaps: false,
                            label: this.getContractedCapacityLabel(powerType),
                            borderColor: contractedCapacityColor,
                            backgroundColor: contractedCapacityColor,
                            pointBorderColor: contractedCapacityColor,
                            data: Measurements.getSlots(timeRange).map(i =>
                                this.feedInPowerTypes.includes(powerType) ? -contractedCapacity : contractedCapacity),
                            stack: `contracted-capacity-${powerType}`,
                            tooltip: {
                                formatter: this.chartUtils.getCustomTooltipFormatter(powerType),
                                stack: AppContext.measurementName(powerType),
                                valuesInverted: this.feedInPowerTypes.includes(powerType)
                            }
                        }
                    });
                }
            });
        }
        return datasets;
    }

    protected shouldRenderPowerType(connectionType: ConnectionType, powerType: DataType): boolean {
        if (!this.feedInPowerTypes.includes(powerType)) {
            return this.options.showPower === true;
        }
        return this.options.showFeedInPower === true
            && !!this.contractedCapacity(connectionType, powerType);
    }

    protected getContractedCapacityLabel(powerType: DataType) {
        return TranslateDirective.getTranslation(
            this.feedInPowerTypes.includes(powerType) ? "Contracted feed-in power" : "Contracted power", true);
    }

    private getPowerDataset(measurementType: DataType): MeasurementDataset {
        const color = DashboardContext.getMeasurementColor(DashboardContext.stacks.currentPeriod, measurementType);
        return {
            measurementType: measurementType,
            dataset: {
                type: "line",
                borderDash: [5],
                order: AppContext.indexOfDataType(measurementType),
                yAxisID: "power",
                spanGaps: false,
                borderColor: color,
                backgroundColor: color,
                pointBorderColor: color,
                label: AppContext.measurementName(measurementType),
                data: this.getPowerData(measurementType),
                stack: AppContext.measurementName(measurementType),
                tooltip: {
                    formatter: this.chartUtils.getCustomTooltipFormatter(measurementType),
                    valuesInverted: this.feedInPowerTypes.includes(measurementType)
                }
            }
        };
    }

    getPowerData(powerDataType: DataType) {
        let powerData: number[] = this.getChartDataForMeasurement(powerDataType, this.timeRange, false).data;
        if (this.feedInPowerTypes.includes(powerDataType)) {
            powerData = powerData.map(d => -d);
        }
        return powerData;
    }

    private setCompareData(data: ChartDataPerMeasurement, timeRange: DashboardTime, emitData: boolean = true) {
        this._rawPreviousData = null;
        if (data?.totals) {
            const setData = (completeness?: CompletenessInfo[]) => {
                const groupedData = this.groupByEntityId ? data.byEntity : data.totals;
                const meterChartLookups = this.groupByEntityId ? this.buildMeterChartLookups(groupedData) : undefined;
                this._rawPreviousData = {
                    ...data,
                    measurements: this.groupDataByMeasurement(groupedData, timeRange, DashboardContext.stacks.lastYear, ChartUtilsService.getComparedYear(this.options.compare, this.timeRange), false, (m) => m.measurements, meterChartLookups, data.electricityConnectionTotalsAvailable),
                    estimatedMeasurements: this.groupDataByMeasurement(groupedData, timeRange, DashboardContext.stacks.lastYear, ChartUtilsService.getComparedYear(this.options.compare, this.timeRange), true, (m) => m.estimatedMeasurements, meterChartLookups, data.electricityConnectionTotalsAvailable),
                    completeness
                };
                this.refreshData(emitData);
            };
            if (this.options.showCompleteness) {
                this.getCompleteness(timeRange).pipe(take(1)).subscribe(setData);
            } else {
                setData();
            }
        } else {
            this.refreshData(emitData);
        }
    }

    private setData(data: ChartDataPerMeasurement, timeRange: DashboardTime, emitData: boolean = true) {
        this._rawData = null;
        if (data.totals) {
            const setData = (completeness?: CompletenessInfo[]) => {
                const displayTimeRange = this.getDisplayTimeRange();
                const groupedData = this.groupByEntityId ? data.byEntity : data.totals;
                const meterChartLookups = this.groupByEntityId ? this.buildMeterChartLookups(groupedData) : undefined;
                this._rawData = {
                    ...data,
                    measurements: this.groupDataByMeasurement(groupedData, displayTimeRange, DashboardContext.stacks.currentPeriod, null, false, (m) => m.measurements, meterChartLookups, data.electricityConnectionTotalsAvailable),
                    estimatedMeasurements: this.groupDataByMeasurement(groupedData, displayTimeRange, DashboardContext.stacks.currentPeriod, null, true, (m) => m.estimatedMeasurements, meterChartLookups, data.electricityConnectionTotalsAvailable),
                    completeness
                };
                this.refreshData(emitData);
            };
            if (this.options.showCompleteness) {
                this.getCompleteness(timeRange).pipe(take(1)).subscribe(setData);
            } else {
                setData();
            }
        } else {
            this.refreshData(emitData);
        }
    }

    showFeedIn = (): boolean => this.options.showFeedIn || this.options.showFeedIn === undefined;
    showConsumption = (): boolean => this.options.showConsumption || this.options.showConsumption === undefined;
    isDataTypeSelected = (type: DataType): boolean => this.options.selectedWeatherTypes.includes(type);

    productionDataTypes = (): DataType[] => [];

    compareFormatter = (c: ChartCompare) => c.relative
        ? (c.comparedYear === 1
            ? TranslateDirective.getTranslation("Last year", true)
            : `${c.comparedYear} ${TranslateDirective.getTranslation("years ago", true)}`)
        : `${c.comparedYear}`;

    compareEquals = (c: ChartCompare, o: ChartCompare) => c && o && c.relative === o.relative && c.comparedYear === o.comparedYear;
    getWeatherInputId = (weatherType: DataType) => this.getInputId('checkbox-' + weatherType + '-' + this.chartOptionType);

    compareYearOptions: ChartCompare[] = [{
        comparedYear: 1,
        enabled: true,
        relative: true
    }].concat(AppContext.getYearsFrom(moment().year() - DashboardContext.maximumYears).map(y => ({
        comparedYear: y,
        enabled: true,
        relative: false
    })).reverse());

    toggleWeather(type: DataType) {
        const options = this.options;
        if (options.selectedWeatherTypes.includes(type)) {
            options.selectedWeatherTypes = options.selectedWeatherTypes.filter(t => t !== type);
        } else {
            options.selectedWeatherTypes = [...options.selectedWeatherTypes, type];
        }
        this.refreshAfterFilterChange(this.filters);
    }

    compareChange(compare: boolean) {
        this.options.compare.enabled = compare;
        if (!this.options.compare.comparedYear) {
            this.options.compare.comparedYear = 1;
            this.options.compare.relative = true;
        }
        this.refreshComparedData();
        if (!this.options.compare.enabled) {
            this.setCompareData(null, null, true);
        }
    }

    comparedYearChange(compareInfo: ChartCompare) {
        this.options.compare = compareInfo
        this.options.compare.enabled = true;
        this.refreshComparedData();
        if (!this.options.compare.enabled) {
            this.setCompareData(null, null,true);
        }
    }

    private refreshComparedData = () => {
        if (this.options.compare.enabled) {
            const compareBaseRange = this.getDisplayTimeRange();
            this.comparedTimeRange = ChartUtilsService.getForYear(compareBaseRange, ChartUtilsService.getComparedYear(this.options.compare, compareBaseRange));
            this.comparedTimeRange.resolution = compareBaseRange.resolution;
            this.getChartData(this.comparedTimeRange).subscribe(d => this.setCompareData(d, this.comparedTimeRange, true));
        }
    }

    private refreshQuarterlyReportData() {
        const compareBaseRange = this.getDisplayTimeRange();
        this.comparedTimeRange = ChartUtilsService.getForYear(compareBaseRange, ChartUtilsService.getComparedYear(this.options.compare, compareBaseRange));
        this.comparedTimeRange.resolution = compareBaseRange.resolution;
        combineLatest([
            this.getChartData(this.timeRange),
            this.getChartData(this.comparedTimeRange)
        ]).subscribe(([currentData, previousData]) => {
            this._rawData = this.buildRawData(currentData, this.getDisplayTimeRange(), DashboardContext.stacks.currentPeriod, null);
            this._rawPreviousData = this.buildRawData(previousData, this.comparedTimeRange, DashboardContext.stacks.lastYear, ChartUtilsService.getComparedYear(this.options.compare, this.timeRange));
            this.refreshData(true);
        });
    }

    private buildRawData(data: ChartDataPerMeasurement, timeRange: DashboardTime, stack: string, comparedYear: number): ChartDataPerMeasurement {
        if (!data?.totals) {
            return null;
        }
        const groupedData = this.groupByEntityId ? data.byEntity : data.totals;
        const meterChartLookups = this.groupByEntityId ? this.buildMeterChartLookups(groupedData) : undefined;
        return {
            ...data,
            measurements: this.groupDataByMeasurement(groupedData, timeRange, stack, comparedYear, false, (m) => m.measurements, meterChartLookups, data.electricityConnectionTotalsAvailable),
            estimatedMeasurements: this.groupDataByMeasurement(groupedData, timeRange, stack, comparedYear, true, (m) => m.estimatedMeasurements, meterChartLookups, data.electricityConnectionTotalsAvailable)
        };
    }

    private getDisplayTimeRange(): DashboardTime {
        if (this.showInQuarterlyReport && this.quarterlyReportDisplayTimeRange) {
            return {
                ...this.quarterlyReportDisplayTimeRange,
                resolution: this.timeRange?.resolution || this.quarterlyReportDisplayTimeRange.resolution
            };
        }
        return this.timeRange;
    }

    private applyQuarterlyReportCutoff(datasets: MeasurementDataset[]): MeasurementDataset[] {
        const displayTimeRange = this.getDisplayTimeRange();
        if (!this.showInQuarterlyReport || !this.quarterlyReportSourceTimeRange || !displayTimeRange) {
            return datasets;
        }
        const cutoffTimestamp = moment(this.quarterlyReportSourceTimeRange.end).valueOf();
        const slots = Measurements.getSlots(displayTimeRange);
        return datasets.map(dataset => {
            if (dataset.dataset.stack !== DashboardContext.stacks.currentPeriod
                || dataset.dataset.yAxisID === "yScatter"
                || dataset.dataset.data?.length !== slots.length) {
                return dataset;
            }
            if (!dataset.dataset.tooltip?.data?.length) {
                dataset.dataset.tooltip.data = cloneDeep(dataset.dataset.data as number[]);
            }
            dataset.dataset.data = dataset.dataset.data.map((value, index) =>
                slots[index]?.startTime >= cutoffTimestamp ? null : value
            );
            return dataset;
        });
    }

    groupByEntityIdChange(groupByEntityId: boolean) {
        this.options.groupByEntityId = groupByEntityId;
        if (groupByEntityId) {
            this.options.splitOffPeak = false;
        }
        this.refreshData(true);
    }

    showGrossProductionChange(showGrossProduction: boolean) {
        this.options.showGrossProduction = showGrossProduction;
        this.refreshData(true);
    }

    showCompletenessChange(showCompleteness: boolean) {
        this.options.showCompleteness = showCompleteness;
        if (!showCompleteness) {
            this.refreshData(true);
            return;
        }
        const currentCompleteness = this._rawData?.completeness
            ? of(this._rawData.completeness)
            : this.getCompleteness(this.timeRange).pipe(take(1));
        const previousCompleteness = !this._rawPreviousData || this._rawPreviousData.completeness
            ? of(this._rawPreviousData?.completeness)
            : this.getCompleteness(this.comparedTimeRange).pipe(take(1));
        combineLatest([currentCompleteness, previousCompleteness]).subscribe(([current, previous]) => {
            if (this._rawData) this._rawData.completeness = current;
            if (this._rawPreviousData) this._rawPreviousData.completeness = previous;
            this.refreshData(true);
        });
    }

    showPowerChange(showPower: boolean) {
        changePowerSelection(this.options, showPower, null, () => this.refreshAfterFilterChange(this.filters));
    }

    showFeedInPowerChange(showFeedInPower: boolean) {
        this.options.showFeedInPower = showFeedInPower;
        this.refreshAfterFilterChange(this.filters);
    }

    showPowerForEanChange(ean: string) {
        changePowerSelection(this.options, true, ean, () => this.refreshAfterFilterChange(this.filters));
    }

    showPowerForEan(connectionType: ConnectionType): string {
        if (this.isPortfolio) {
            return null;
        }
        const connections = this.activePrimaryConnections(connectionType);
        if (connections.length === 1) {
            return this.options.showPower ? connections[0].source.connection.info.code : null
        }
        return this.selectedActivePowerConnectionCode(connections);
    }

    private selectedPowerConnectionCode(connectionType: ConnectionType): string {
        if (this.isPortfolio) {
            return null;
        }
        const connections = this.activePrimaryConnections(connectionType);
        return connections.length === 1
            ? connections[0].source.connection.info.code
            : this.selectedActivePowerConnectionCode(connections);
    }

    private activePrimaryConnections(connectionType?: ConnectionType): SourceInfo[] {
        return this.getSelectedConnections(connectionType).filter(connection => {
            const meters = connection.source.connection.meters;
            return this.isCurrentConnection(connection)
                && meters?.some(meter => meter.active !== false && meter.info.type === MeterType.PRIMARY);
        });
    }

    private isCurrentConnection(connection: SourceInfo): boolean {
        const desiredEndDate = connection.source.connection.info?.desiredEndDate;
        return !desiredEndDate || Date.parse(desiredEndDate) > Date.now();
    }

    private selectedActivePowerConnectionCode(connections: SourceInfo[]): string {
        const activeEans = connections.map(connection => connection.source.connection.info.code);
        return activeEans.includes(this.options.showPowerForEan)
            ? this.options.showPowerForEan
            : activeEans[0];
    }

    groupByEntityIdEnabled = (): boolean => this.options.groupByEntityId && this.options.groupByEntityIdPossible && this.groupByEntityId;

    toggleShowAsTable() {
        this.options.showAsTable = !this.options.showAsTable;
        this.refreshData(true)
    }

    measurementTypes = (): DataType[] => [];

    measurementIntermediateLink = (): Map<DataType, DataType[]> => new Map<DataType, DataType[]>();

    consumptionProductionLink = (): Map<DataType, DataType> => new Map<DataType, DataType>();

    measurementTypesMapped = (): { [key: string]: DataType[] } => ({});

    productionDataTypesMapped = (): { [key: string]: DataType[] } => ({})

    powerDataTypeMapped = (): { [key: string]: DataType } => {
        return {
            [ConnectionType.Electricity]: DataType.electricityPower,
            [ConnectionType.Gas]: DataType.gasPower,
            [ConnectionType.Heat]: DataType.heatPower,
            [ConnectionType.Water]: DataType.waterPower,
            [ConnectionType.ChargePoint]: DataType.chargePointPower,
            [ConnectionType.Cooling]: DataType.coolingPower
        };
    }

    powerDataTypes(connectionType: ConnectionType): DataType[] {
        const powerType = this.powerDataTypeMapped()[connectionType];
        return powerType ? [powerType] : [];
    }

    private getConsumptionAndProductionData(rawData: MeasurementDataset[]): MeasurementDataset[] {
        return this.groupByEntityIdEnabled()
            ? Object.entries(lodash.groupBy(rawData, r => r.entityId))
                .flatMap(s => this.createDatasets(s[1], rawData))
            : this.createDatasets(rawData, rawData);
    }

    private createDatasets(dataset: MeasurementDataset[], rawData: MeasurementDataset[]) {
        let dataOfMeasurements = this.getDataOfMeasurements(dataset, this.measurementTypes());
        if (this.groupByEntityIdEnabled()) {
            dataOfMeasurements = this.getAggregatedConsumptionData(dataOfMeasurements, "y1", true);
        }
        const consumptionData = this.processConsumptionData(dataOfMeasurements, rawData);
        const prodData = this.processProductionData(this.getDataOfMeasurements(dataset, this.productionDataTypes()));
        const aggregatedProdData = this.getAggregatedProductionData(prodData, "y1");
        return this.applyProductionData(consumptionData, prodData)
            .concat(prodData)
            .concat(this.applyProductionData(this.getAggregatedConsumptionData(consumptionData, "y1"), aggregatedProdData))
            .concat(aggregatedProdData);
    }

    protected measurementDisplayFactor(type: DataType): number {
        return type === DataType.heatConsumption ? DashboardContext.gjToM3Rate : 1;
    }

    getDataOfMeasurements = (rawData: MeasurementDataset[], measurementTypes: DataType[]): MeasurementDataset[] =>
        rawData.filter(d => measurementTypes.includes(d.measurementType))
            .map(d => {
                const measurementSet = cloneDeep(d);
                const factor = this.measurementDisplayFactor(measurementSet.measurementType);
                if (factor !== 1) {
                    measurementSet.dataset.data = measurementSet.dataset.data.map(v => (v as number) * factor);
                }
                return measurementSet;
            });

    private processConsumptionData(consumptionData: MeasurementDataset[], allData: MeasurementDataset[]) {
        if (consumptionData.some(dataset => !canCalculateResidual(dataset.connectionTotalAuthorized))) {
            return consumptionData;
        }
        const createMainConsumptionSet = (parentDataset: MeasurementDataset,
                                          childDatasets: MeasurementDataset[]): MeasurementDataset[] => {
            parentDataset = cloneDeep(parentDataset);
            const residualMeter = this.findCalculatedResidualFor(parentDataset.entityId);
            if (residualMeter && !isMeterVisible(residualMeter.meter)) {
                return childDatasets;
            }
            parentDataset.dataset.order = 10000;
            if (residualMeter) {
                const unallocatedLabel = TranslateDirective.getTranslation('Unallocated consumption', true) as string;
                const configuredLabel = residualMeter.meter.details?.label;
                parentDataset.dataset.label = calculatedResidualLabel(configuredLabel, unallocatedLabel);
                parentDataset.dataset.tooltip.residualMeterLabel = parentDataset.dataset.label;
                parentDataset.dataset.tooltip.calculatedResidual = true;
                parentDataset.dataset.tooltip.order = 1000;
            }
            parentDataset.dataset.tooltip.mainMeterTotalData = cloneDeep(parentDataset.dataset.data as number[]);
            const totalSummary = parentDataset.dataset.tooltip.totalConsumptionSummary;
            if (totalSummary) {
                // Keep this muted summary identical for every display combination.
                totalSummary.detail = TranslateDirective.getTranslation('Main meter', true);
            }
            parentDataset.dataset.tooltip.formatter = this.mainConsumptionSetTooltipFormatter(parentDataset);
            const remainderColor = residualMeter ? CALCULATED_RESIDUAL_COLOR
                : parentDataset.dataset.backgroundColor as string;
            const mainMeterColor = parentDataset.dataset.backgroundColor as string;
            const childTotals = parentDataset.dataset.data.map((_, index) =>
                lodash.sum(childDatasets.map(child => Number(child.dataset.data[index] || 0))));
            parentDataset.dataset.data = calculateResidualSeries(
                parentDataset.dataset.data.map(Number),
                childDatasets.map(child => child.dataset.data.map(Number)));
            parentDataset.dataset.tooltip.data = cloneDeep(parentDataset.dataset.data as number[]);
            this.applyRemainderPattern(parentDataset, childTotals, remainderColor, mainMeterColor);
            return [parentDataset];
        }
        if (this.groupByEntityIdEnabled()) {
            const link = this.measurementIntermediateLink();
            const hasMeasurementLink = consumptionData.some(c => link.has(c.measurementType));
            const hasParentMeterSelected = consumptionData.some(c => c.isParentMeterData);
            let data: MeasurementDataset[] = [];
            const consumptionDataAggregated = this.getAggregatedConsumptionData(consumptionData, "y1", true)
                .concat(consumptionData.filter(c => c.dataset.yAxisID === "y1Aggregated"));
            if (hasMeasurementLink) { // Measurement linking is done by Consumption - intermediate consumption
                link.forEach((value, key) => {
                    let mainConsumptionSet = consumptionDataAggregated.find(c => c.measurementType === key);
                    if (mainConsumptionSet) {
                        const intermediateConsumptions = allData.filter(m => value.includes(m.measurementType)
                            && belongsToResidualConnection(mainConsumptionSet.connectionId, m.connectionId));
                        data = data.concat(createMainConsumptionSet(mainConsumptionSet, intermediateConsumptions));
                    }
                });
                return data;
            }
            if (hasParentMeterSelected) { // Parent meters is done by entity where all children have been selected. This is values - values
                let mainConsumptionSet = consumptionDataAggregated.find(c => c.isParentMeterData);
                if (mainConsumptionSet) {
                    const intermediateConsumptions = allData.filter(m => !m.isParentMeterData
                        && belongsToResidualConnection(mainConsumptionSet.connectionId, m.connectionId));
                    data = data.concat(createMainConsumptionSet(mainConsumptionSet, intermediateConsumptions));
                }
                return data;
            }
        }
        return consumptionData;
    }

    private findCalculatedResidualFor(parentMeterId?: string) {
        const selectedMeterSources = [...(this.filters?.allSelectedSources || []),
            ...(this.filters?.cleanedSources || [])]
            .filter(source => source.type === EntityType.meter
                && source.connectionType === ConnectionType.Electricity);
        const selectedConnectionCandidates = [
            ...this.getSelectedConnections(ConnectionType.Electricity), ...selectedMeterSources
        ]
            .flatMap(source => {
                const selectedLocation = source.source.location;
                const liveLocation = this.liveLocations.get(selectedLocation?.locationId) || selectedLocation;
                const connection = liveLocation?.connections?.find(candidate =>
                    candidate.connectionId === source.source.connection?.connectionId) || source.source.connection;
                const entity = calculatedResidualEntity(source.source.organisation, liveLocation, connection);
                return entity ? [{meter: entity.meter, connection}] : [];
            });
        const locations = [...(this.filters?.allSelectedSources || []), ...(this.filters?.cleanedSources || [])]
            .map(source => source.source?.location)
            .filter(Boolean)
            .filter((location, index, values) => values.findIndex(candidate =>
                candidate.locationId === location.locationId) === index)
            .map(location => this.liveLocations.get(location.locationId) || location);
        const locationCandidates = locations.flatMap(location => location.connections || [])
            .flatMap(connection => (connection.meters || [])
                .filter(isCalculatedResidual)
                .map(meter => ({meter, connection})));
        const candidates = [...selectedConnectionCandidates, ...locationCandidates]
            .filter((candidate, index, values) => values.findIndex(other =>
                other.connection.connectionId === candidate.connection.connectionId) === index);
        const scopedCandidates = candidates.filter(candidate => !parentMeterId
            || candidate.connection.connectionId === parentMeterId
            || candidate.connection.meters?.some(meter => meter.meterId === parentMeterId));
        if (scopedCandidates.length === 1) {
            return scopedCandidates[0];
        }
        return candidates.length === 1 ? candidates[0] : null;
    }

    private applyDiagonalPattern(d: MeasurementDataset, color: string = d.dataset.backgroundColor as string, direction: 'ltr' | 'rtl' = 'ltr') {
        d.dataset.backgroundColor = ChartUtilsService.createDiagonalPattern(color, direction);
        d.dataset.borderColor = color;
        d.dataset.borderWidth = 1;
    }

    private applyRemainderPattern(d: MeasurementDataset, childTotals: number[], remainderColor: string,
                                  mainMeterColor: string) {
        const pattern = ChartUtilsService.createDiagonalPattern(remainderColor, 'rtl');
        const hasUnallocatedRemainder = (value: unknown, index: number) =>
            childTotals[index] > 0 && Number(value || 0) > 0;
        d.dataset.backgroundColor = (d.dataset.data as number[]).map((value, index) =>
            hasUnallocatedRemainder(value, index) ? pattern : mainMeterColor);
        d.dataset.borderColor = (d.dataset.data as number[]).map((value, index) =>
            hasUnallocatedRemainder(value, index) ? remainderColor : mainMeterColor);
        d.dataset.borderWidth = 1;
    }

    private processProductionData(productionData: MeasurementDataset[]) {
        return productionData.map(p => {
            const d = cloneDeep(p);
            if (this.options.nettedConsumption) {
                this.applyDiagonalPattern(d, "#adadad");
                d.dataset.tooltip.valuesInverted = false;
            } else {
                d.dataset.data = d.dataset.data.map(r => -r);
            }
            return d;
        });
    }

    private applyProductionData(dataset: MeasurementDataset[], productionDataset: MeasurementDataset[]): MeasurementDataset[] {
        if (!this.options.nettedConsumption) {
            return dataset;
        }
        return cloneDeep(dataset).map(d => {
            const productionType = this.consumptionProductionLink().get(d.measurementType);
            if (productionType) {
                const prodDataset = productionDataset.find(s => s.measurementType === productionType);
                if (prodDataset) {
                    d.dataset.data = d.dataset.data.map((d, i) => (d as number) - (prodDataset.dataset.data[i] as number));
                    d.dataset.label = d.dataset.tooltip?.residualMeterLabel
                        || (d.measurementType === DataType.electricityConsumption ? "Net consumption" : "Net consumption off-peak");
                }
            }
            return d;
        })
    }

    private getAggregatedConsumptionData(dataset: MeasurementDataset[], axis: string, replace: boolean = false) {
        const mappedData = this.measurementTypesMapped();
        return Object.entries(mappedData).flatMap(e => {
            return this.getAggregatedData(dataset.filter(d => e[1].includes(d.measurementType)), axis, e[0] as DataType, replace);
        });
    }

    private getAggregatedProductionData(dataset: MeasurementDataset[], axis: string, replace: boolean = false) {
        const mappedData = this.productionDataTypesMapped();
        return Object.entries(mappedData).flatMap(e => {
            return this.getAggregatedData(dataset.filter(d => e[1].includes(d.measurementType)), axis, e[0] as DataType, replace);
        });
    }

    private getAggregatedData(dataset: MeasurementDataset[], axis: string, m: DataType, replace: boolean = false): MeasurementDataset[] {
        const axisData = dataset.filter(d => d && d.dataset["yAxisID"] === axis);
        if (axisData.length && axis === "y1" && (this.getYAxis() !== axis || replace)) {
            var obj = axisData.find(a => a.measurementType === m) || axisData[0]
            const aggregatedData = cloneDeep({
                ...obj,
                dataset: {
                    ...obj.dataset,
                    label: obj.dataset.tooltip?.residualMeterLabel
                        || this.getLabel(m, this.groupByEntityIdEnabled() ? obj.entityId : null)
                }
            });
            if (!this.groupByEntityIdEnabled()) {
                delete aggregatedData.entityId;
            }
            aggregatedData.dataset["yAxisID"] = this.getYAxis();
            aggregatedData.dataset.data = aggregatedData.dataset.data.map((r, i) =>
                lodash.sum(axisData.flatMap(r => r.dataset.data[i])));
            aggregatedData.dataset.tooltip.hasData = aggregatedData.dataset.data.map((_, i) =>
                axisData.some(d => d.dataset.tooltip?.hasData?.[i] ?? d.dataset.data[i] != null));
            return [aggregatedData];
        }
        return [];
    }

    protected getYAxis() {
        return "y1";
    }

    private getCompletenessAxis = (completeness: CompletenessInfo[], isPrevious: boolean = false, comparedYear: number = null): MeasurementDataset => {
        let color = Rgba.fromString("rgba(163, 98, 201, 100)");
        if (isPrevious) {
            color = color.tint(0.25).greyScale();
        }
        const allConnectionsSelected = this.getSelectedConnections()
            .map(s => s.source.connection)
            .filter(s => this.connectionTypes.includes(s.info.connectionType as ConnectionType));
        const selection = allConnectionsSelected.map(s => s.connectionId);
        const completenessRecords = completeness
            .filter(c => selection.includes(c.connectionId))
            .filter(c => this.connectionTypes.includes(c.connectionType))
            .flatMap(c => c.meterCompleteness.map(c => c.meterView)
                .filter(m => m));
        const timeRange = isPrevious ? this.comparedTimeRange : this.timeRange;
        return {
            dataset: {
                type: "line",
                borderDash: [5],
                order: 10000,
                yAxisID: "completeness",
                spanGaps: false,
                label: TranslateDirective.getTranslation("Completeness", true),
                borderColor: color.toString(),
                backgroundColor: color.toString(),
                pointBorderColor: color.toString(),
                data: Measurements.getSlots(timeRange)
                    .map(i => {
                        return lodash.round(AppContext.computeCompleteness(completenessRecords, {
                            start: moment(i.startTime).toISOString(),
                            end: moment(i.endTime).toISOString()
                        }, allConnectionsSelected.filter(c => !c.meters.length)), 3);
                    }),
                stack: isPrevious ? DashboardContext.stacks.lastYear : DashboardContext.stacks.currentPeriod,
                tooltip: {
                    ...this.comparisonTooltipGroup(comparedYear,
                        isPrevious ? DashboardContext.stacks.lastYear : DashboardContext.stacks.currentPeriod),
                    formatter: (ctx: TooltipModel<any>, value: number) => this.chartUtils.percentageFormatter(value),
                    higherValueIsBetter: true
                }
            }
        };
    }

    protected groupDataByMeasurement(data: MeasurementsResult[], dateRange: TimeRange, stack: string, comparedYear: number, estimated: boolean, source: (m: MeasurementsResult) => { [P in DataType]?: AggregatedDataPoint[] }, meterChartLookups?: MeterChartLookups, connectionTotalAuthorized?: boolean): { [P in DataType]?: MeasurementDataset[] } {
        const measurements: { [P in DataType]?: MeasurementDataset[] } = {};
        const lookups = this.groupByEntityId
            ? meterChartLookups || this.buildMeterChartLookups(data)
            : undefined;
        data.forEach((m, i) => {
            Object.entries(source(m)).forEach((e) => {
                const measurementType = this.effectiveMeasurementType(e[0] as DataType, m.entityId, lookups);
                if (!measurementType) return;
                if (!measurements[measurementType]) {
                    measurements[measurementType] = [];
                }
                let dataset: MeasurementDataset;

                if (this.groupByEntityId) {
                    const entities = lookups.entitiesByDataType.get(measurementType) || [];
                    const entityIndex = lookups.entityIndexByDataType.get(measurementType)?.get(m.entityId) ?? -1;
                    const color = entities.length > 1 ?
                        interpolate(entityIndex + 1, 0, entities.length, -0.5, 0.5) : 0;
                    dataset = this.createChartDatasetForEntity(e[1], measurementType, dateRange, stack,
                        m.entityId, color, comparedYear, estimated, lookups);
                } else {
                    dataset = this.createChartDataset(e[1], measurementType, dateRange, stack, comparedYear, AppContext.measurementName(measurementType), 0, estimated);
                }
                if (dataset) {
                    dataset.connectionTotalAuthorized = connectionTotalAuthorized;
                    measurements[measurementType].push(dataset);
                }
            });
        });
        return measurements;
    }

    private buildMeterChartLookups(data: MeasurementsResult[]): MeterChartLookups {
        return buildMeterChartLookups(
            data,
            this.liveLocations,
            this.filters?.allSelectedSources || [],
            this.filters?.cleanedSources || [],
            EntityType.meter
        );
    }

    private effectiveMeasurementType(measurementType: DataType, entityId: string, lookups?: MeterChartLookups): DataType | null {
        const sourceInfo = lookups ? lookups.meterSources.get(entityId)
            : [...(this.filters?.allSelectedSources || []), ...(this.filters?.cleanedSources || [])]
                .find(source => source.id === entityId && source.type === EntityType.meter);
        const entity = lookups
            ? lookups.liveMeterEntities.get(entityId) || sourceInfo?.source
            : this.findLiveMeterEntity(entityId) || sourceInfo?.source;
        if (!entity?.meter) return measurementType;
        if (!isMeterVisible(entity.meter)) return null;
        if (measurementType !== DataType.electricityIntermediateConsumption) return measurementType;
        if (entity.meter.info?.type === MeterType.GROSS_PRODUCTION) {
            return DataType.electricityGrossProduction;
        }
        if (isChargePointMeter(entity)) return DataType.chargePointConsumption;
        if (effectiveMeterType(entity.meter) === MeterType.GROSS_PRODUCTION) {
            return DataType.electricityGrossProduction;
        }
        return measurementType;
    }

    private createChartDatasetForEntity(data: AggregatedDataPoint[], measurementType: DataType, dateRange: TimeRange, stack: string, entityId: string, color: number, lastYearNumber?: number, estimated?: boolean, lookups?: MeterChartLookups): MeasurementDataset {
        const chartDataset = this.createChartDataset(data, measurementType, dateRange, stack,
            lastYearNumber, this.getLabel(measurementType, entityId, lookups), color, estimated);
        chartDataset.entityId = entityId;
        const entity = lookups?.liveMeterEntities.get(entityId)
            || lookups?.meterSources.get(entityId)?.source;
        chartDataset.connectionId = residualConnectionId(
            entity?.connection, entity?.location?.connections || []);
        chartDataset.dataset.order += color;
        if (lookups) {
            chartDataset.isParentMeterData = lookups.parentMeterDataById.get(entityId);
        } else {
            const selectionIds = this.filters?.cleanedSources?.map(s => s.id);
            chartDataset.isParentMeterData = this.filters?.cleanedSources?.find(
                s => s.type === EntityType.meter && s.id === chartDataset.entityId && s.source.getChildIds()?.length)
                ?.source.getChildIds().every(i => selectionIds.includes(i));
        }
        return chartDataset.dataset.data.every(d => d === 0) ? null : chartDataset;
    }

    getLabel(measurementType: DataType, entityId?: string, lookups?: MeterChartLookups) {
        const liveEntity = lookups ? lookups.liveMeterEntities.get(entityId) : this.findLiveMeterEntity(entityId);
        const selectedSourceName = lookups ? lookups.selectedSourceNames.get(entityId)
            : this.filters?.allSelectedSources?.find(s => s.id === entityId)?.name;
        // An unlabelled charge-point meter exposes only a technical access-point ID.
        // The aggregate chart is clearer without it, while custom labels remain useful.
        const sourceName = liveEntity && isChargePointMeter(liveEntity)
            ? liveEntity.meter.details?.label || liveEntity.meter.info?.label || ""
            : liveEntity ? RefdataUtils.meterFormatter(liveEntity)
            : selectedSourceName ?? "";
        return `${AppContext.measurementName(measurementType)}${sourceName ? " - " + sourceName : ""}`;
    }

    private findLiveMeterEntity(entityId?: string): Entity | null {
        if (!entityId) return null;
        for (const location of this.liveLocations.values()) {
            for (const connection of location.connections || []) {
                const meter = (connection.meters || []).find(candidate => candidate.meterId === entityId);
                if (meter) {
                    return <Entity><unknown>{location, connection, meter};
                }
            }
        }
        return null;
    }

    createChartDataset(data: AggregatedDataPoint[], measurementType: DataType, dateRange: DashboardTime, stack: string, lastYearNumber?: number, label: string = AppContext.measurementName(measurementType), tint: number = 0, estimated?: boolean): MeasurementDataset {
        const chartColorRgba = DashboardContext.getMeasurementColor(stack, measurementType, tint);
        const groupedData = Measurements.groupByTimeFrames(Measurements.dataPointsToChartData(data), dateRange, AppContext.getAggregationMethod(measurementType));
        const hasData = Measurements.appendAggregatedDataToSlots(data, Measurements.getSlots(dateRange))
            .map(s => s.values.length > 0);
        const order = AppContext.indexOfDataType(measurementType);
        return {
            measurementType: measurementType,
            dataset: {
                order: estimated ? order * 1000 : order,
                yAxisID: "y1",
                pointBorderColor: "rgb(0,0,0,0)",
                borderColor: chartColorRgba,
                backgroundColor: estimated ? "#FFFFFF" : chartColorRgba,
                borderWidth: estimated ? 1 : 0,
                label: estimated ? `${label} (${TranslateDirective.getTranslation("estimated", true)})` : label,
                data: groupedData.data,
                stack: stack,
                tooltip: {
                    formatter: this.chartUtils.getCustomTooltipFormatter(measurementType),
                    ...this.comparisonTooltipGroup(lastYearNumber, stack),
                    valuesInverted: AppContext.productionMeasurements.includes(measurementType),
                    labelOverride: "",
                    hasData,
                    estimated: estimated
                }
            }
        };
    }

    getChartDataForMeasurement = (measurement: DataType, dateRange: DashboardTime, isPrevious) =>
        this.getChartDataForMeasurements(measurement, dateRange,
            isPrevious ? this._rawPreviousData?.totals : this._rawData?.totals);

    getChartDataForMeasurements = (measurement: DataType, dateRange: DashboardTime, measurements: MeasurementsResult[]) =>
        Measurements.groupByTimeFrames(measurements ? measurements.flatMap(m =>
                Measurements.dataPointsToChartData(m.measurements[measurement])) : [],
            dateRange, AppContext.getAggregationMethod(measurement));

    private getWeatherDataset = (weatherType: DataType, timeRange: DashboardTime, isPrevious: boolean): MeasurementDataset => {
        const stack = isPrevious ? DashboardContext.stacks.lastYear : DashboardContext.stacks.currentPeriod;
        const color = DashboardContext.getMeasurementColor(stack, weatherType);
        return {
            measurementType: weatherType,
            dataset: {
                type: "line",
                order: AppContext.indexOfDataType(weatherType),
                yAxisID: weatherType,
                spanGaps: false,
                borderColor: color,
                backgroundColor: color,
                pointBorderColor: color,
                label: AppContext.measurementName(weatherType),
                data: this.getChartDataForMeasurement(weatherType, timeRange, isPrevious).data,
                stack: stack,
                tooltip: {
                    formatter: this.chartUtils.getCustomTooltipFormatter(weatherType),
                    ...this.comparisonTooltipGroup(isPrevious
                        ? ChartUtilsService.getComparedYear(this.options.compare, this.timeRange) : null, stack)
                }
            }
        };
    }

    private comparisonTooltipGroup(comparedYear: number | null | undefined,
                                   defaultStack?: string): {stack?: string; stackYear?: number; groupOrder?: number} {
        if (comparedYear == null) return defaultStack ? {stack: defaultStack, groupOrder: 0} : {};
        return this.options.compare?.relative
            ? {stack: TranslateDirective.getTranslation("Last year", true), groupOrder: 10}
            : {stackYear: comparedYear, groupOrder: 10};
    }

    private mainConsumptionSetTooltipFormatter(dataset: MeasurementDataset): (ctx: TooltipModel<any>, value: number, index: number) => string {
        return (ctx: TooltipModel<any>, value: number, index: number) =>
            this.chartUtils.defaultTooltipFormatter(value, dataset.measurementType);
    }

    compareIncidents = (a: IncidentOption, b: IncidentOption) => a?.value === b?.value;
}

export interface MeasurementData {
    labels?: string[];
    datasets: MeasurementDataset[];
}

export interface MeasurementDataset<TType extends ChartType = 'line'> {
    displayUnit?: string;
    comparisonStack?: string;
    measurementType?: DataType;
    entityId?: string;
    connectionId?: string;
    estimated?: boolean;
    isParentMeterData?: boolean;
    connectionTotalAuthorized?: boolean;
    dataset: ChartDatasetExtended<TType>;
}

export type ChartDatasetExtended<TType extends keyof ChartTypeRegistry = 'line'> = ChartDataset<TType, (number | Point | [number, number] | BubbleDataPoint)[]> & {
    measurementType?: DataType;
    buildingType?: BuildingType;
    tooltipLabels?: string[];
    tooltip?: ChartDatasetTooltip | any;
};

interface ChartDatasetTooltip {
    stack?: string;
    order?: number;
    groupOrder?: number;
    valuesInverted?: boolean;
    higherValueIsBetter?: boolean;
    estimated?: boolean;
    stackYear?: number;
    formatter?: (ctx: TooltipModel<any>, value: number, index: number) => string;
    labelOverride?: string | string[];
    borderColors?: string[];
    data?: number[];
    mainMeterTotalData?: number[];
    hasData?: boolean[];
    totalConsumptionSummary?: {
        label: string;
        detail?: string;
        data: number[];
        formatter?: (ctx: TooltipModel<any>, value: number, index?: number) => string;
    };
}

export interface ChartCompleteness {
    completeness: number;
    color: string;
    title: string;
}

export interface IncidentOption {
    label: string;
    value: string;
}

export const INCIDENT_OPTIONS: IncidentOption[] = [
    { label: 'Missing data', value: AlertType.MissingData },
    { label: 'Disconnected', value: AlertType.Disconnected },
    { label: 'Contracted power exceeded', value: AlertType.ContractedPowerExceeded },
    { label: 'Contracted feed-in power exceeded', value: AlertType.ContractedFeedInPowerExceeded },
];
